import React, { useEffect, useRef, useState } from 'react';

export type LibraryTab = 'manifesto' | 'guide';

interface Props {
  onClose:     () => void;
  initialTab?: LibraryTab;
}

const SERIF = "'Cormorant Garamond', 'Georgia', 'Times New Roman', serif";
const SANS  = "'Inter', -apple-system, BlinkMacSystemFont, system-ui, sans-serif";

const BG_PANEL  = '#0a0f1e';
const BG_DEEP   = '#050816';
const INK       = '#e8ecf5';
const INK_SOFT  = '#9aa3bd';
const INK_FAINT = '#5a6486';
const PURPLE    = '#a78bfa';
const GOLD      = '#fcd34d';
const TEAL      = '#7dd3fc';
const GREEN     = '#34d399';
const RULE      = 'rgba(255,255,255,0.08)';

// ─────────────────────────────────────────────────────────────────────────────
// Shared atoms
// ─────────────────────────────────────────────────────────────────────────────

function Eyebrow({ children, color = GOLD }: { children: React.ReactNode; color?: string }) {
  return (
    <p style={{
      fontFamily:    SANS,
      fontSize:      10,
      letterSpacing: '0.32em',
      textTransform: 'uppercase',
      color,
      margin:        '0 0 18px',
      fontWeight:    500,
    }}>
      {children}
    </p>
  );
}

function Heading({ children, level = 2 }: { children: React.ReactNode; level?: 1 | 2 }) {
  const size = level === 1 ? 'clamp(32px, 4.6vw, 44px)' : 'clamp(22px, 3vw, 30px)';
  return (
    <h2 style={{
      fontFamily:    SERIF,
      fontWeight:    500,
      fontStyle:     'italic',
      fontSize:      size,
      lineHeight:    1.14,
      letterSpacing: '-0.008em',
      color:         INK,
      margin:        '0 0 16px',
    }}>
      {children}
    </h2>
  );
}

function Lede({ children }: { children: React.ReactNode }) {
  return (
    <p style={{
      fontFamily: SERIF,
      fontWeight: 400,
      fontSize:   17,
      lineHeight: 1.65,
      color:      INK_SOFT,
      margin:     '0 0 28px',
      maxWidth:   '58ch',
    }}>
      {children}
    </p>
  );
}

function Para({ children }: { children: React.ReactNode }) {
  return (
    <p style={{
      fontFamily: SANS,
      fontWeight: 300,
      fontSize:   14,
      lineHeight: 1.78,
      color:      INK_SOFT,
      margin:     '0 0 16px',
      maxWidth:   '62ch',
    }}>
      {children}
    </p>
  );
}

function Act({ numeral, title, kicker, children }: {
  numeral: string; title: string; kicker: string; children: React.ReactNode;
}) {
  return (
    <section style={{
      padding:   '40px 0 8px',
      borderTop: `1px solid ${RULE}`,
      marginTop: 24,
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 16, marginBottom: 14 }}>
        <span style={{
          fontFamily: SERIF, fontStyle: 'italic', fontSize: 34,
          color: PURPLE, opacity: 0.7, lineHeight: 1,
        }}>
          {numeral}
        </span>
        <span style={{
          fontFamily: SANS, fontSize: 9, letterSpacing: '0.3em',
          textTransform: 'uppercase', color: INK_FAINT,
        }}>
          {kicker}
        </span>
      </div>
      <Heading>{title}</Heading>
      {children}
    </section>
  );
}

function Step({ n, title, children }: { n: number; title: string; children: React.ReactNode }) {
  return (
    <div style={{
      display:        'grid',
      gridTemplateColumns: '48px 1fr',
      gap:            18,
      padding:        '20px 0',
      borderTop:      `1px solid ${RULE}`,
    }}>
      <div style={{
        fontFamily: SERIF, fontStyle: 'italic', fontWeight: 500,
        fontSize:   36, lineHeight: 1,
        color:      PURPLE, opacity: 0.75,
      }}>
        {String(n).padStart(2, '0')}
      </div>
      <div>
        <h3 style={{
          fontFamily: SERIF, fontWeight: 500, fontSize: 22,
          color: INK, margin: '0 0 10px', lineHeight: 1.25,
        }}>
          {title}
        </h3>
        {children}
      </div>
    </div>
  );
}

function Pull({ children, accent = PURPLE }: { children: React.ReactNode; accent?: string }) {
  return (
    <blockquote style={{
      borderLeft: `2px solid ${accent}`,
      padding:    '4px 0 4px 18px',
      margin:     '14px 0',
      maxWidth:   '56ch',
    }}>
      <p style={{
        fontFamily: SERIF, fontStyle: 'italic', fontWeight: 400,
        fontSize:   17, lineHeight: 1.55,
        color:      INK, margin: 0,
      }}>
        {children}
      </p>
    </blockquote>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Manifesto (The Why)
// ─────────────────────────────────────────────────────────────────────────────

function ManifestoContent() {
  return (
    <div>
      <Eyebrow>A Manifesto in Three Acts</Eyebrow>

      <h1 style={{
        fontFamily:    SERIF,
        fontStyle:     'italic',
        fontWeight:    400,
        fontSize:      'clamp(34px, 5.4vw, 52px)',
        lineHeight:    1.08,
        letterSpacing: '-0.012em',
        color:         INK,
        margin:        '0 0 24px',
        maxWidth:      '17ch',
      }}>
        Where the <span style={{ color: PURPLE }}>Marketplace</span> meets the Truth.
      </h1>

      <Lede>
        Agoracle is a peer-to-peer prediction marketplace in which the wisdom of the crowd is
        sealed by social consensus and revealed by mathematics. No house. No edge. Only the gap
        between what people believe and what the market proves.
      </Lede>

      <Act numeral="I" kicker="The Agora" title="A Marketplace Without a House">
        <Para>
          The ancient Athenian agora was not merely a place of trade — it was a space where
          truth was tested in public. Agoracle inherits that lineage. Every market here is
          minted peer-to-peer. There is{' '}
          <em style={{ color: PURPLE }}>no black-box AI manipulation</em> of the odds, no house
          algorithm tilting the floor. The price is set by{' '}
          <em style={{ color: PURPLE }}>Dynamic Consensus Balancing</em>: as more citizens stake
          their conviction on an outcome, that outcome's share of the price rises in proportion.
          The number on the screen is the ward's collective conviction, in plain view — not the
          verdict of a hidden formula.
        </Para>
        <Pull accent={PURPLE}>
          Through Social Forking, any participant may challenge a market by forking it — sharper
          question, tighter resolution, new lens on the same event. The truest framing wins
          liquidity. Settlement walks the ancestry chain, paying tribute to the Root Architect
          whose original question gave the lineage life.
        </Pull>
      </Act>

      <Act numeral="·" kicker="Ground Zero · Norwich" title="Thirteen wards, chosen on purpose.">
        <Para>
          We did not launch in London. We launched in Norwich — thirteen wards, six contesting
          parties, candidate saturation at a decade high. Most city elections narrow to a
          two-party tussle; Norwich 2026 fractures. Labour, Green, Conservative, Liberal
          Democrat, Reform UK, and a long tail of independents and minor parties all contest
          overlapping ground.
        </Para>
        <Para>
          For a prediction engine this is the perfect stress test. Thirteen wards means
          thirteen parallel micro-markets of belief, each with its own divergence between
          doorstep sentiment and headline polling. If Agoracle surfaces truth here — where
          the signal is noisy, the ground game is fragmented, and{' '}
          <em style={{ color: PURPLE }}>The Field</em> is crowded with outsiders — it will
          surface truth anywhere. Norwich is the crucible.
        </Para>
      </Act>

      <Act numeral="II" kicker="The Fortress" title="Privacy Without Compromise">
        <Para>
          We collect what we must. Nothing more. No tracking pixels, no behavioural
          surveillance, no data brokerage, no algorithmic profiling of your participation.
          Your wagers are your own — encrypted in transit, sovereign at rest, never sold.
        </Para>
        <Para>
          The Isle of Man jurisdiction is not a tax haven; it is a regulatory fortress. Every
          money-moving path runs the same compliance gate — verification, self-exclusion,
          deposit limits — written into the kernel and never short-circuited. Audit trails are
          append-only by architecture, not by promise.
        </Para>
      </Act>

      <Act numeral="III" kicker="The Oracle" title="Verifiable Truth, Public Settlement">
        <Para>
          Powered by the Social Forking Oracle, every market settles in two phases — an initial
          declaration, then a challenge window during which any participant may contest the
          outcome by posting a bond. Truth is not handed down; it is converged upon, in public,
          with every step on the ledger.
        </Para>
        <Para>
          When a market resolves, a small{' '}
          <em style={{ color: GOLD }}>Agora Levy</em> — 3% on net winnings — is split three
          ways: a portion to the Isle of Man exchequer in recognition of the regulatory
          fortress, a portion to the Root Architect whose question opened the inquiry, and a
          portion to the commons that sustain the protocol. The house keeps nothing.
        </Para>
      </Act>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Guide (The How)
// ─────────────────────────────────────────────────────────────────────────────

function GuideContent() {
  return (
    <div>
      <Eyebrow color={TEAL}>A Field Guide in Four Steps</Eyebrow>

      <h1 style={{
        fontFamily:    SERIF,
        fontWeight:    500,
        fontSize:      'clamp(30px, 4.6vw, 44px)',
        lineHeight:    1.14,
        letterSpacing: '-0.008em',
        color:         INK,
        margin:        '0 0 18px',
        maxWidth:      '18ch',
      }}>
        How to trade on Agoracle.
      </h1>

      <Lede>
        Every market answers a single question by splitting the probability space across
        outcomes. You buy the outcome you believe is undervalued; you are paid out in full if
        it resolves true. Here is the whole flow.
      </Lede>

      <Step n={1} title="Read the divergence.">
        <Para>
          Each outcome shows two numbers. <em style={{ color: TEAL }}>Price</em> is what the
          crowd's money says. <em style={{ color: GOLD }}>Vibe</em> is what human and machine
          sentiment says. When Vibe is higher than Price, the market hasn't caught up to the
          signal yet — that gap is your edge.
        </Para>
        <Pull accent={TEAL}>
          Green arrow = Vibe outruns Price. Act before the market closes the gap.
        </Pull>
      </Step>

      <Step n={2} title="Understand The Field.">
        <Para>
          Not every election has five named candidates. Some markets have dozens of
          long-tail outcomes — parties too small to list individually, independents, write-ins.
          <em style={{ color: PURPLE }}> The Field</em> is the single slice that represents{' '}
          every outcome not named explicitly. A bet on The Field pays out if{' '}
          <em>any</em> unlisted outcome wins.
        </Para>
        <Para>
          In Norwich 2026, The Field covers the British Democratic Party, the Communist Party,
          and every independent contesting a ward. Small in price, wide in reach.
        </Para>
      </Step>

      <Step n={3} title="State your conviction.">
        <Para>
          Choose an outcome, type the number of <em style={{ color: PURPLE }}>Truth Units</em>{' '}
          you wish to claim, read the quote box. Each Truth Unit is a claim on a future reality
          — if validated by the Oracle, it pays out at full conviction (100 credits). Before
          you confirm, the quote shows the <em>cost</em>, the <em>price after trade</em> (how
          far your order moves the market), and the <em>price impact</em> in green, yellow, or
          red. No hidden fees at trade time.
        </Para>
        <Pull accent={GOLD}>
          A 5% longshot earns 3× more Divergence Points than a 50% favourite. Courage on hard
          calls is rewarded — the Prophecy Multiplier is baked into the maths.
        </Pull>
      </Step>

      <Step n={4} title="The Agora Levy — 3% on winnings only.">
        <Para>
          There is <em style={{ color: INK }}>no fee to trade</em>. A flat{' '}
          <em style={{ color: GOLD }}>3% Agora Levy</em> is taken only from{' '}
          <em>net winnings</em> at settlement. If you don't win, you pay nothing extra.
          Winners pay the way for everyone.
        </Para>
        <div style={{
          display:        'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap:            10,
          margin:         '8px 0 4px',
        }}>
          {[
            { pct: '50%', label: 'Platform Operations',   color: TEAL,   desc: 'Infrastructure, WebSockets, storage — no ads, no sponsors.' },
            { pct: '25%', label: 'Root Architect',        color: GREEN,  desc: 'The person whose question opened the lineage.' },
            { pct: '25%', label: 'Oracle Reward Pool',    color: GOLD,   desc: 'Distributed to participants by Divergence Points accuracy.' },
          ].map(r => (
            <div key={r.label} style={{
              background:   BG_DEEP,
              border:       `1px solid ${r.color}33`,
              borderTop:    `2px solid ${r.color}`,
              borderRadius: 4,
              padding:      '14px 14px 12px',
            }}>
              <p style={{
                fontFamily: SERIF, fontSize: 26, fontStyle: 'italic',
                color: r.color, margin: 0, lineHeight: 1,
              }}>
                {r.pct}
              </p>
              <p style={{
                fontFamily: SANS, fontSize: 9, letterSpacing: '0.22em',
                textTransform: 'uppercase', color: r.color,
                margin: '8px 0 6px', fontWeight: 600,
              }}>
                {r.label}
              </p>
              <p style={{
                fontFamily: SANS, fontSize: 11, lineHeight: 1.55,
                color: INK_SOFT, margin: 0, fontWeight: 300,
              }}>
                {r.desc}
              </p>
            </div>
          ))}
        </div>
      </Step>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Modal shell
// ─────────────────────────────────────────────────────────────────────────────

export default function KnowledgeBase({ onClose, initialTab = 'manifesto' }: Props): React.JSX.Element {
  const bodyRef     = useRef<HTMLDivElement>(null);
  const manifestoRef = useRef<HTMLDivElement>(null);
  const guideRef    = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState<LibraryTab>(initialTab);

  // Escape closes
  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Jump to the initial section when the modal first mounts
  useEffect(() => {
    const target = initialTab === 'guide' ? guideRef.current : manifestoRef.current;
    if (target && bodyRef.current) {
      bodyRef.current.scrollTo({ top: target.offsetTop - 12, behavior: 'auto' });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function jumpTo(tier: LibraryTab): void {
    const target = tier === 'guide' ? guideRef.current : manifestoRef.current;
    if (target && bodyRef.current) {
      bodyRef.current.scrollTo({ top: target.offsetTop - 12, behavior: 'smooth' });
      setActive(tier);
    }
  }

  // Track which tier is currently on screen so the jump-nav underline follows scroll
  useEffect(() => {
    const body = bodyRef.current;
    if (!body) return;
    function onScroll() {
      if (!body || !guideRef.current) return;
      const guideTop = guideRef.current.offsetTop - body.clientHeight * 0.35;
      setActive(body.scrollTop >= guideTop ? 'guide' : 'manifesto');
    }
    body.addEventListener('scroll', onScroll, { passive: true });
    return () => body.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <>
      <link
        rel="stylesheet"
        href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;1,400;1,500&family=Inter:wght@300;400;500;600&display=swap"
      />

      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0, zIndex: 1000,
          backgroundColor: 'rgba(2,4,14,0.82)',
          backdropFilter: 'blur(6px)',
          WebkitBackdropFilter: 'blur(6px)',
          display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
          padding: '24px 16px',
          overflowY: 'auto',
        }}
      >
        <div
          onClick={e => e.stopPropagation()}
          style={{
            width:        '100%',
            maxWidth:     760,
            margin:       '24px auto',
            background:   BG_PANEL,
            border:       `1px solid ${RULE}`,
            borderTop:    `2px solid ${PURPLE}`,
            borderRadius: 6,
            boxShadow:    '0 30px 80px rgba(0,0,0,0.55)',
          }}
        >
          {/* ── Header ────────────────────────────────────── */}
          <div style={{
            padding:        '22px 32px 14px',
            display:        'flex',
            alignItems:     'baseline',
            justifyContent: 'space-between',
            gap:            16,
          }}>
            <div>
              <p style={{
                fontFamily: SERIF, fontWeight: 500, fontSize: 22,
                color: INK, margin: 0, letterSpacing: '0.005em',
              }}>
                The <span style={{ color: PURPLE, fontStyle: 'italic' }}>Agoracle</span> Library
              </p>
              <p style={{
                fontFamily: SANS, fontSize: 9, letterSpacing: '0.3em',
                textTransform: 'uppercase', color: INK_FAINT,
                margin: '4px 0 0',
              }}>
                The Why · The How
              </p>
            </div>
            <button
              onClick={onClose}
              aria-label="Close"
              style={{
                background: 'none', border: 'none',
                color: INK_FAINT, fontSize: 20, cursor: 'pointer',
                padding: '0 4px', lineHeight: 1,
                fontFamily: SERIF,
              }}
              onMouseEnter={e => { e.currentTarget.style.color = INK; }}
              onMouseLeave={e => { e.currentTarget.style.color = INK_FAINT; }}
            >
              ✕
            </button>
          </div>

          {/* ── Jump-nav (tiered anchors, not tabs) ───────── */}
          <div style={{
            display:      'flex',
            gap:          0,
            padding:      '0 32px',
            borderBottom: `1px solid ${RULE}`,
          }}>
            {([
              { id: 'manifesto', label: 'The Manifesto', sub: 'I · The Why' },
              { id: 'guide',     label: 'User Guide',    sub: 'II · The How' },
            ] as Array<{ id: LibraryTab; label: string; sub: string }>).map(t => {
              const isActive = active === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => jumpTo(t.id)}
                  style={{
                    background:  'none', border: 'none', cursor: 'pointer',
                    padding:     '14px 0',
                    marginRight: 32,
                    borderBottom: `2px solid ${isActive ? PURPLE : 'transparent'}`,
                    color:       isActive ? INK : INK_FAINT,
                    transition:  'color 0.18s, border-bottom-color 0.18s',
                    textAlign:   'left',
                  }}
                >
                  <div style={{
                    fontFamily: SERIF, fontSize: 18, fontWeight: 500,
                    letterSpacing: '0.005em', lineHeight: 1.1,
                  }}>
                    {t.label}
                  </div>
                  <div style={{
                    fontFamily: SANS, fontSize: 9, letterSpacing: '0.24em',
                    textTransform: 'uppercase',
                    color: isActive ? PURPLE : INK_FAINT,
                    marginTop: 3, fontWeight: 500,
                  }}>
                    {t.sub}
                  </div>
                </button>
              );
            })}
          </div>

          {/* ── Body: tiered (Manifesto → Guide), single scroll ─ */}
          <div ref={bodyRef} style={{
            padding:    '32px 32px 24px',
            maxHeight:  '72vh',
            overflowY:  'auto',
          }}>
            <div ref={manifestoRef}><ManifestoContent /></div>

            {/* Tier divider */}
            <div style={{
              margin:     '56px 0 44px',
              display:    'flex',
              alignItems: 'center',
              gap:        16,
            }}>
              <div style={{ flex: 1, height: 1, background: RULE }} />
              <span style={{
                fontFamily: SANS, fontSize: 9, letterSpacing: '0.32em',
                textTransform: 'uppercase', color: INK_FAINT, fontWeight: 500,
              }}>
                II · The How
              </span>
              <div style={{ flex: 1, height: 1, background: RULE }} />
            </div>

            <div ref={guideRef}><GuideContent /></div>
          </div>

          {/* ── Footer ───────────────────────────────────── */}
          <div style={{
            padding:        '16px 32px 22px',
            borderTop:      `1px solid ${RULE}`,
            display:        'flex',
            justifyContent: 'space-between',
            alignItems:     'center',
            gap:            12,
          }}>
            <span style={{
              fontFamily: SANS, fontSize: 9, letterSpacing: '0.22em',
              textTransform: 'uppercase', color: INK_FAINT,
            }}>
              Agoracle · MMXXVI · Isle of Man
            </span>
            <button
              onClick={onClose}
              style={{
                background:   'transparent',
                border:       `1px solid ${PURPLE}`,
                color:        PURPLE,
                padding:      '8px 22px',
                borderRadius: 3,
                fontFamily:   SANS,
                fontWeight:   600,
                fontSize:     10,
                letterSpacing:'0.24em',
                textTransform:'uppercase',
                cursor:       'pointer',
                transition:   'background 0.18s, color 0.18s',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = PURPLE; e.currentTarget.style.color = '#0b0418'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = PURPLE; }}
            >
              Understood
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
